import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PublicLinkDialog({ projectId, trigger }) {
    const [open, setOpen] = useState(false);
    const [links, setLinks] = useState([]);
    const [form, setForm] = useState({ contractor_name: "", trade: "" });

    const load = async () => {
        try {
            const data = await api.listPublicLinks(projectId);
            setLinks(data);
        } catch (e) {
            console.error("PublicLinkDialog load failed:", e);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line
    }, [open]);

    const create = async () => {
        try {
            await api.createPublicLink(projectId, form);
            setForm({ contractor_name: "", trade: "" });
            load();
            toast.success("Lien créé");
        } catch {
            toast.error("Erreur");
        }
    };

    const url = (token) => `${window.location.origin}/public/devis/${token}`;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button
                        variant="outline"
                        data-testid="public-link-btn"
                        className="rounded-none border-ink"
                    >
                        <Link2 className="w-4 h-4 mr-2" strokeWidth={1.6} />
                        Lien public
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        Liens publics — saisie en ligne par l'artisan
                    </DialogTitle>
                </DialogHeader>
                <div className="text-xs text-ink-soft border-l-2 border-accent pl-3 mb-2">
                    Générez un lien unique à envoyer à l'artisan. Il pourra
                    saisir son devis directement en ligne sans création de
                    compte.
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-1">
                        <Label className="stat-label">
                            Nom (pré-rempli)
                        </Label>
                        <Input
                            data-testid="public-name"
                            className="rounded-none mt-1.5"
                            value={form.contractor_name}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    contractor_name: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="col-span-1">
                        <Label className="stat-label">
                            Corps de métier (pré-rempli)
                        </Label>
                        <Input
                            data-testid="public-trade"
                            className="rounded-none mt-1.5"
                            value={form.trade}
                            onChange={(e) =>
                                setForm({ ...form, trade: e.target.value })
                            }
                        />
                    </div>
                    <div className="col-span-1 flex items-end">
                        <Button
                            data-testid="public-create"
                            className="rounded-none bg-ink text-white hover:bg-ink/90 w-full"
                            onClick={create}
                        >
                            Créer le lien
                        </Button>
                    </div>
                </div>

                <div className="mt-3 border border-border max-h-[40vh] overflow-y-auto">
                    <div className="px-3 py-2 bg-[#FAFAFA] border-b border-border stat-label">
                        Liens existants ({links.length})
                    </div>
                    {links.length === 0 ? (
                        <div className="px-3 py-6 text-sm text-ink-soft text-center">
                            Aucun lien pour ce chantier.
                        </div>
                    ) : (
                        links.map((l) => (
                            <div
                                key={l.token}
                                className="px-3 py-2.5 border-b border-border last:border-0 flex items-center justify-between gap-2"
                            >
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">
                                        {l.contractor_name || "Lien anonyme"}{" "}
                                        <span className="text-ink-soft text-xs">
                                            — {l.trade || "tout corps"}
                                        </span>
                                    </div>
                                    <div className="text-xs text-ink-soft truncate num">
                                        {url(l.token)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {l.submitted ? (
                                        <span className="pill border-ok text-ok bg-ok/10">
                                            <CheckCircle2 className="w-3 h-3" />
                                            Reçu
                                        </span>
                                    ) : (
                                        <span className="pill border-warn text-warn bg-warn/10">
                                            En attente
                                        </span>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="rounded-none h-7"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                url(l.token)
                                            );
                                            toast.success("Lien copié");
                                        }}
                                    >
                                        <Copy className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Fermer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
